import { useEffect, useState } from "react";
import api, { fmtError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import { LEAD_INDUSTRIES } from "@/lib/leadOptions";
import { toast } from "sonner";

// Shared cross-kind edit dialog for the unified People entities:
//   contact | lead | marketing | client_primary
// Each kind lives in a different collection so we route to the right
// PATCH endpoint and field name dynamically.
const KIND_LABEL = {
  contact: "Contact",
  lead: "Lead",
  marketing: "Cold",
  client_primary: "Client",
};
const KIND_CLASS = {
  contact: "bg-blue-50 text-blue-800 border-blue-200",
  lead: "bg-amber-50 text-amber-800 border-amber-200",
  marketing: "bg-slate-50 text-slate-800 border-slate-200",
  client_primary: "bg-primary-50 text-primary-700 border-primary-200",
};
const KIND_ENDPOINT = {
  contact: "/contacts",
  lead: "/leads",
  marketing: "/marketing-contacts",
  client_primary: "/clients",
};
const KIND_NAME_FIELD = {
  contact: "name",
  lead: "name",
  marketing: "name",
  client_primary: "contact_person",
};

export default function EditUnifiedContactDialog({ open, onOpenChange, row, onSaved }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [form, setForm] = useState({
    name: "", company_name: "", designation: "", email: "", phone: "",
  });
  const [saving, setSaving] = useState(false);
  // 30.docx — changing a lead's Industry Sector moves its marketing contact
  // from the old industry category to the new one automatically.
  const [industry, setIndustry] = useState("");
  // Admin-only lead-owner reassignment (corrections doc 2 — legacy leads
  // imported by admin must be transferable to the right salesperson).
  const [ownerId, setOwnerId] = useState("");
  const [users, setUsers] = useState([]);
  useEffect(() => {
    if (open && isAdmin && row?.kind === "lead") {
      api.get("/users").then((r) => setUsers(r.data || [])).catch(() => setUsers([]));
      setOwnerId(row.owner_id || "");
    }
  }, [open, isAdmin, row]);

  useEffect(() => {
    if (open && row) {
      setForm({
        name: row.name || "",
        company_name: row.company_name || "",
        designation: row.designation || "",
        email: row.email || "",
        phone: row.phone || "",
      });
      setIndustry(row.industry || "");
    }
  }, [open, row]);

  if (!row) return null;
  const endpoint = KIND_ENDPOINT[row.kind];
  const nameField = KIND_NAME_FIELD[row.kind];
  const kindLabel = KIND_LABEL[row.kind] || row.kind;

  const submit = async () => {
    if (!form.name.trim()) { toast.error("Name required"); return; }
    if (row.kind === "lead" && !industry) { toast.error("Industry Sector is required."); return; }
    setSaving(true);
    try {
      const norm = (v) => (v && String(v).trim()) || null;
      const payload = {
        [nameField]: form.name.trim(),
        company_name: norm(form.company_name),
        email: norm(form.email),
        phone: norm(form.phone),
      };
      if (row.kind !== "client_primary") {
        payload.designation = norm(form.designation);
      }
      if (row.kind === "lead" && industry && industry !== row.industry) {
        payload.industry = industry;
      }
      if (isAdmin && row.kind === "lead" && ownerId && ownerId !== row.owner_id) {
        payload.owner_id = ownerId;
      }
      await api.patch(`${endpoint}/${row.id}`, payload);
      toast.success(`${kindLabel} updated`);
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(fmtError(err));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl max-w-md max-h-[90vh] overflow-y-auto" data-testid="edit-unified-contact-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-extrabold flex items-center gap-2">
            Edit
            <Badge variant="outline" className={`text-[10px] uppercase tracking-wider ${KIND_CLASS[row.kind] || ""}`}>
              {kindLabel}
            </Badge>
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs font-semibold">
              {row.kind === "client_primary" ? "Contact person" : "Name"} *
            </Label>
            <Input
              className="rounded-2xl h-11 mt-1.5"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="edit-unified-name"
            />
          </div>
          <div>
            <Label className="text-xs font-semibold">Company</Label>
            <Input
              className="rounded-2xl h-11 mt-1.5"
              value={form.company_name}
              onChange={(e) => setForm({ ...form, company_name: e.target.value })}
              data-testid="edit-unified-company"
            />
          </div>
          {row.kind !== "client_primary" && (
            <div>
              <Label className="text-xs font-semibold">Designation</Label>
              <Input
                className="rounded-2xl h-11 mt-1.5"
                value={form.designation}
                onChange={(e) => setForm({ ...form, designation: e.target.value })}
                placeholder="e.g. Maintenance Manager"
                data-testid="edit-unified-designation"
              />
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-semibold">Phone</Label>
              <Input
                className="rounded-2xl h-11 mt-1.5"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                data-testid="edit-unified-phone"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold">Email</Label>
              <Input
                className="rounded-2xl h-11 mt-1.5"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="edit-unified-email"
              />
            </div>
          </div>
          {row.kind === "lead" && (
            <div>
              <Label className="text-xs font-semibold">Industry Sector *</Label>
              <Select value={industry || "__none"} onValueChange={(v) => setIndustry(v === "__none" ? "" : v)}>
                <SelectTrigger className="rounded-2xl h-11 mt-1.5" data-testid="edit-unified-industry-select">
                  <SelectValue placeholder="Pick industry…" />
                </SelectTrigger>
                <SelectContent className="rounded-2xl max-h-72">
                  {LEAD_INDUSTRIES.map((o) => <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-[10px] text-muted-foreground mt-1">Stored on the linked CRM contact as industry information.</p>
            </div>
          )}
          {isAdmin && row.kind === "lead" && users.length > 0 && (
            <div>
              <Label className="text-xs font-semibold">Lead Owner</Label>
              <Select value={ownerId || "__none"} onValueChange={(v) => setOwnerId(v === "__none" ? "" : v)}>
                <SelectTrigger className="rounded-2xl h-11 mt-1.5" data-testid="edit-unified-owner-select">
                  <SelectValue placeholder="Select owner" />
                </SelectTrigger>
                <SelectContent className="rounded-2xl">
                  {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-[10px] text-muted-foreground mt-1">Admin only — reassign this lead to the salesperson who actually owns it.</p>
            </div>
          )}
          <p className="text-[11px] text-muted-foreground pt-1">
            Quick-edit of the common fields. For kind-specific fields (lead industry, client GSTIN, etc.) open the record from its own tab.
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-2xl" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="rounded-2xl" data-testid="edit-unified-save">
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
